export type ChatRole = "user" | "assistant" | "system";

export interface ChatMessage {
  kind: "message";
  id: string;
  role: ChatRole;
  content: string;
  // When the message entered the transcript: stamped client-side for a live turn,
  // hydrated from the persisted `created` for a loaded conversation. Optional so a
  // legacy/in-flight item without a stamp still renders.
  createdAt?: Date;
  // The tool calls an assistant turn made, in call order: its tool transcript.
  toolCalls?: ChatToolCall[];
}

export interface ChatToolCall {
  tool: string;
  // Null when the server dropped the arguments as too large to send.
  args: Record<string, unknown> | null;
  // How the call ended; absent while it runs, or when the turn stopped before it finished.
  outcome?: ToolCallOutcome;
}

export interface ToolCallOutcome {
  ok: boolean;
  // A short preview of what the call returned, or its error; never the full result.
  summary: string;
  durationMs: number | null;
}

export interface ChatError {
  kind: "error";
  id: string;
  content: string;
  // The user turn this error belongs to, so its Retry re-sends the right text
  // even when a later turn has since moved lastUserContentRef on.
  userContent?: string;
  // The failure code (backend error event, or an HTTP/network mapping). Surfaced
  // subtly for support; drives the actionable copy in `content`.
  code?: string;
  // Backend log-correlation id (#20.D) from the terminal error frame, shown as a
  // copyable id so support can pivot a failed session to its log line.
  requestId?: string | null;
}

export interface ChatApproval {
  kind: "approval";
  id: string;
  // Opaque id minted by the server for this pending approval; echoed back to the
  // approvals endpoint to resolve it.
  approvalId: string;
  // The gated tool (e.g. "execute") and the server-authored prompt naming the
  // privileged operation(s) that need approval.
  tool: string;
  message: string;
  // Lifecycle of the card: awaiting the user, then the decision they made, or a
  // settled terminal state. `expired` = the run ended (timeout/stop/done) before the
  // user answered; `failed` = the decision errored (resume couldn't run, or the decline
  // POST failed). A non-pending card is disabled.
  status: "pending" | "accepted" | "declined" | "expired" | "failed";
  // Which decision failed, persisted on the item (not just card-local state) so the right
  // failure copy survives a remount. Only set alongside `status: "failed"`.
  failedAction?: "accept" | "decline";
}

export type ChatTranscriptItem = ChatMessage | ChatError | ChatApproval;

export interface ReferencedFile {
  resourceId: string;
  name: string;
  fileRevisionId?: string;
  origin: ReferencedFileOrigin;
}

export type ReferencedFileOrigin = "attachment" | "chat-save";

// The upload display name is user-editable and may already carry the extension,
// so only append it when it isn't already there (case-insensitive) to avoid
// duplicated suffixes like "report.pdf.pdf".
export function buildReferencedFileName(displayName: string, extension: string): string {
  if (!extension) return displayName;
  const suffix = `.${extension}`;
  return displayName.toLowerCase().endsWith(suffix.toLowerCase()) ? displayName : `${displayName}${suffix}`;
}
